/**
 * Stores > Setup > Store Info at /stores/setup/info.
 *
 * One page per store for Store Ops and IT: pick any store from the dropdown
 * and see the same Store Info and Store Hours boxes the Sweet Spot My Store
 * page shows a store manager, plus three things only HQ needs: the store's
 * network carrier and connection type, an append-only note log, and a tile
 * into its photo and document folders.
 *
 * The two detail boxes are `StoreInfoCard` / `StoreHoursCard` from
 * @ampersandbrands/ui, the same components The Sweet Spot renders, so the two
 * apps can never drift on how a store reads. The Store Network and Notes cards
 * below them are Cockpit-only and reuse the shared cards' own chrome (the
 * preset's semantic token classes, which Cockpit maps to olive and cream in
 * ui/src/index.css) so all four sit together as one family.
 *
 * Gated by its own stores.info slug. Every /v1/stores/info/* 404 or 503 means
 * the backend is not deployed yet and degrades to the quiet placeholder.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FolderOpen, Network, StickyNote } from 'lucide-react';
import { StoreHoursCard, StoreInfoCard } from '@ampersandbrands/ui';
import { Pill } from '@/components/Pill';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { formatDateTime } from '@/lib/dateUtils';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { isNotAvailable, storeLabel } from '@/lib/useStores';
import { useSession } from '@/lib/useSession';
import {
  useAddStoreNote,
  useSaveStoreNetwork,
  useStoreInfoDetails,
  useStoreInfoStores,
  useStoreNotes,
  type StoreNetwork,
  type StoreNetworkAccess,
  type StoreNetworkCarrier,
  type StoreNote,
} from '@/lib/useStoreInfo';
import {
  readLastStoreInfoStore,
  resolveStoreInfoStore,
  storeStatusKind,
  storeStatusLabel,
  writeLastStoreInfoStore,
  type StoreStatusKind,
} from './storeInfoParam';
import {
  AccessDeniedCard,
  ErrorCard,
  NotAvailableCard,
  StoresHeader,
  inputCls,
  labelCls,
  primaryBtnCls,
} from './storesShared';

// The shared cards' chrome, so the two local cards below sit in the same
// family as StoreInfoCard / StoreHoursCard rather than inventing a second one.
const CARD_CLS = 'rounded-lg border border-line bg-surface shadow-card';

const STATUS_TONE: Record<StoreStatusKind, string> = {
  open: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  closed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  other: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

const CARRIERS: Array<{ value: StoreNetworkCarrier; label: string }> = [
  { value: 'granite', label: 'Granite' },
  { value: 'uniti', label: 'Uniti' },
  { value: 'other', label: 'Other' },
  { value: 'unknown', label: 'Unknown' },
];

const ACCESS_TYPES: Array<{ value: StoreNetworkAccess; label: string }> = [
  { value: 'wired', label: 'Wired' },
  { value: 'cellular', label: 'Cellular' },
  { value: 'unknown', label: 'Unknown' },
];

const NETWORK_NOTE_MAX = 500;

type Toast = { kind: 'ok' | 'err'; text: string };

export function StoreInfo() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, 'stores.info', user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const urlStore = searchParams.get('store');

  const storesQ = useStoreInfoStores(canView);
  const stores = useMemo(() => storesQ.data ?? [], [storesQ.data]);
  // Read once per mount: after that the URL is the source of truth, so a pick
  // made in another tab never yanks this one to a different store.
  const [remembered] = useState(readLastStoreInfoStore);
  const storeNo = resolveStoreInfoStore(urlStore, remembered, stores);

  const setStore = (v: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('store', v);
        return next;
      },
      { replace: true },
    );
  };

  // Remember whatever is actually on screen, including the default pick, so
  // the next visit lands on the same store without needing a link.
  useEffect(() => {
    if (storeNo) writeLastStoreInfoStore(storeNo);
  }, [storeNo]);

  const detailsQ = useStoreInfoDetails(storeNo, canView);
  const notesQ = useStoreNotes(storeNo, canView);

  const picked = stores.find((s) => s.store_no === storeNo) ?? null;
  const details = detailsQ.data ?? null;
  const status = details?.editable?.store_status ?? picked?.store_status ?? null;

  if (user && !canView) return <AccessDeniedCard what="Store Info" />;

  const header = (
    <StoresHeader
      title="Store Info"
      subtitle="One page per store: address, hours, manager, network, notes, and the store photo and document folders."
      actions={<ToolPinToggle toolSlug="stores-info" toolName="Store Info" />}
    />
  );

  // The picker list is the page's spine: without it there is no store to show,
  // so a not-yet-deployed backend is reported once, here.
  if (isNotAvailable(storesQ.error) || isNotAvailable(detailsQ.error)) {
    return (
      <div>
        {header}
        <NotAvailableCard what="Store Info" />
      </div>
    );
  }

  return (
    <div>
      {header}

      <div className="mb-5 w-full sm:w-80">
        <label htmlFor="store-info-store" className={labelCls}>
          Store
        </label>
        <select
          id="store-info-store"
          value={storeNo ?? ''}
          disabled={stores.length === 0}
          onChange={(e) => setStore(e.target.value)}
          className={inputCls}
        >
          {stores.length === 0 && <option value="">{storesQ.isLoading ? 'Loading stores...' : 'No stores'}</option>}
          {stores.map((s) => (
            <option key={s.store_no} value={s.store_no}>
              {storeLabel(s.store_no, s.store_name)}
            </option>
          ))}
        </select>
      </div>

      {storesQ.error ? (
        <ErrorCard message={storesQ.error.message} />
      ) : !storeNo ? (
        storesQ.isLoading ? (
          <div className="h-64 animate-pulse rounded-lg bg-amp-cream dark:bg-amp-dark-surface-warm" />
        ) : (
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No stores to show.</p>
        )
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-xl font-semibold text-amp-ink dark:text-amp-dark-ink">
              {storeLabel(storeNo, picked?.store_name ?? null)}
            </h2>
            <Pill tone={STATUS_TONE[storeStatusKind(status)]}>{storeStatusLabel(status)}</Pill>
          </div>

          {detailsQ.error ? (
            <ErrorCard message={detailsQ.error.message} />
          ) : detailsQ.isLoading ? (
            <div className="grid gap-5 lg:grid-cols-2">
              <div className="h-64 animate-pulse rounded-lg bg-amp-cream dark:bg-amp-dark-surface-warm" />
              <div className="h-64 animate-pulse rounded-lg bg-amp-cream dark:bg-amp-dark-surface-warm" />
            </div>
          ) : (
            <>
              <div className="grid gap-5 lg:grid-cols-2">
                <StoreInfoCard details={details} />
                <StoreHoursCard details={details} />
              </div>
              <StoreNetworkPanel key={storeNo} storeNo={storeNo} network={details?.network ?? null} />
            </>
          )}

          <StoreNotesPanel
            key={`notes-${storeNo}`}
            storeNo={storeNo}
            notes={notesQ.data ?? []}
            loading={notesQ.isLoading}
            error={notesQ.error}
          />

          <Link
            to={`/stores/setup/info/${storeNo}/files`}
            className={`${CARD_CLS} flex items-center gap-3 px-4 py-3 hover:bg-surface-raised`}
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-raised text-accent">
              <FolderOpen size={16} aria-hidden />
            </span>
            <span className="text-[15px] font-semibold text-ink">Photos and Documents</span>
          </Link>
        </div>
      )}
    </div>
  );
}

// Card chrome matching StoreInfoCard / StoreHoursCard from the shared package.
function LocalCard({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div className={CARD_CLS}>
      <div className="flex items-center gap-2.5 border-b border-line px-4 py-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-raised text-accent">
          {icon}
        </span>
        <span className="text-[15px] font-semibold text-ink">{title}</span>
      </div>
      <div className="px-4 py-3">{children}</div>
    </div>
  );
}

function ToastLine({ toast }: { toast: Toast }) {
  return (
    <div
      role="status"
      className={`mt-3 rounded-md border px-3 py-2 text-sm ${
        toast.kind === 'ok'
          ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/40 dark:text-green-200'
          : 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/40 dark:text-red-200'
      }`}
    >
      {toast.text}
    </div>
  );
}

// Mounted with the store number as its key, so switching stores remounts it
// with that store's saved values rather than keeping the previous edits.
function StoreNetworkPanel({ storeNo, network }: { storeNo: string; network: StoreNetwork | null }) {
  const [carrier, setCarrier] = useState<StoreNetworkCarrier>(network?.carrier ?? 'unknown');
  const [access, setAccess] = useState<StoreNetworkAccess>(network?.access ?? 'unknown');
  const [note, setNote] = useState(network?.note ?? '');
  const [toast, setToast] = useState<Toast | null>(null);
  const save = useSaveStoreNetwork(storeNo);

  const onSave = () => {
    setToast(null);
    save.mutate(
      { carrier, access, note: note.trim() || null },
      {
        onSuccess: () => setToast({ kind: 'ok', text: 'Network saved.' }),
        onError: (err) => setToast({ kind: 'err', text: `Could not save: ${err.message}` }),
      },
    );
  };

  return (
    <LocalCard icon={<Network size={16} aria-hidden />} title="Store Network">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={`net-carrier-${storeNo}`} className={labelCls}>
            Carrier
          </label>
          <select
            id={`net-carrier-${storeNo}`}
            value={carrier}
            onChange={(e) => setCarrier(e.target.value as StoreNetworkCarrier)}
            className={inputCls}
          >
            {CARRIERS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={`net-access-${storeNo}`} className={labelCls}>
            Access
          </label>
          <select
            id={`net-access-${storeNo}`}
            value={access}
            onChange={(e) => setAccess(e.target.value as StoreNetworkAccess)}
            className={inputCls}
          >
            {ACCESS_TYPES.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-3">
        <label htmlFor={`net-note-${storeNo}`} className={labelCls}>
          Note
        </label>
        <input
          id={`net-note-${storeNo}`}
          type="text"
          value={note}
          maxLength={NETWORK_NOTE_MAX}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Circuit ID, install date, anything worth knowing"
          className={inputCls}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button type="button" onClick={onSave} disabled={save.isPending} className={primaryBtnCls}>
          {save.isPending ? 'Saving...' : 'Save'}
        </button>
        {network?.updated_at && (
          <span className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Updated by {network.updated_by ?? 'unknown'} on {formatDateTime(network.updated_at)}
          </span>
        )}
      </div>

      {toast && <ToastLine toast={toast} />}
    </LocalCard>
  );
}

function StoreNotesPanel({
  storeNo,
  notes,
  loading,
  error,
}: {
  storeNo: string;
  notes: StoreNote[];
  loading: boolean;
  error: Error | null;
}) {
  const [draft, setDraft] = useState('');
  const [toast, setToast] = useState<Toast | null>(null);
  const add = useAddStoreNote(storeNo);

  const onAdd = () => {
    const body = draft.trim();
    if (!body) return;
    setToast(null);
    add.mutate(body, {
      onSuccess: () => {
        setDraft('');
        setToast({ kind: 'ok', text: 'Note added.' });
      },
      onError: (err) => setToast({ kind: 'err', text: `Could not add note: ${err.message}` }),
    });
  };

  return (
    <LocalCard icon={<StickyNote size={16} aria-hidden />} title="Notes">
      <label htmlFor={`note-draft-${storeNo}`} className={labelCls}>
        Add a note
      </label>
      <textarea
        id={`note-draft-${storeNo}`}
        rows={3}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="What happened, what was checked, what is still open"
        className={inputCls}
      />
      <div className="mt-2">
        <button
          type="button"
          onClick={onAdd}
          disabled={add.isPending || !draft.trim()}
          className={primaryBtnCls}
        >
          {add.isPending ? 'Adding...' : 'Add note'}
        </button>
      </div>

      {toast && <ToastLine toast={toast} />}

      <div className="mt-4">
        {error ? (
          <ErrorCard message={error.message} />
        ) : loading ? (
          <div className="h-20 animate-pulse rounded-lg bg-amp-cream dark:bg-amp-dark-surface-warm" />
        ) : notes.length === 0 ? (
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No notes yet.</p>
        ) : (
          <ul className="divide-y divide-line">
            {notes.map((n) => (
              <li key={n.id} className="py-2.5">
                <p className="whitespace-pre-wrap text-[14px] text-ink">{n.body}</p>
                <p className="mt-1 text-xs text-ink-muted">
                  {n.author_email} &middot; {formatDateTime(n.created_at)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </LocalCard>
  );
}
